import { Button, Icon, Typography, useTheme } from "@outschool/backpack";
import { faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  Form,
  IconButton,
  Input
} from "@outschool/ui-legacy-component-library";
import { pxToRem } from "@outschool/ui-utils";
import React from "react";

import { useSearchFilters } from "../../lib/useSearchFilters";

export default function ClassSearchForm({
  query,
  setQuery,
  onSubmit,
  placeholder,
  onInputFocus = () => {},
  getInputProps = () => ({}),
  spellCheck = true,
  useGlobalNavUi,
  useTutoringSearchUi
}: {
  query: string;
  setQuery: (query: string) => void;
  onSubmit?: (query: string) => void;
  placeholder?: string;
  onInputFocus?: () => void;
  getInputProps?: () => object;
  spellCheck?: boolean;
  useOneOnOneSearchUI?: boolean;
  useGlobalNavUi?: boolean;
  useTutoringSearchUi?: boolean;
}) {
  const { t } = useTranslation("client\\components\\search\\ClassSearchForm");
  placeholder ??= t`Any topic or teacher`;
  const { setFilters: setSearchFilters } = useSearchFilters();

  const theme = useTheme();

  const inputRef = React.createRef<HTMLInputElement>();

  const handleSubmit = e => {
    e.preventDefault();

    if (inputRef.current) {
      inputRef.current.blur();
    }
    handleBlur();
  };

  const handleBlur = () => {
    if (onSubmit) {
      onSubmit(query);
    } else {
      // Default behavior: redirect to listings page with query & user filters
      // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      setSearchFilters("Query", {
        q: query && query.length ? query : null
      });
    }
  };

  const handleChange = e => {
    setQuery(e.target.value);
  };

  const handleClear = () => {
    // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    setSearchFilters("Query", {
      q: null
    });
    setQuery("");
  };

  if (!!useGlobalNavUi) {
    return (
      <Form
        sx={{
          backgroundColor: "white",
          border: "3px solid",
          borderColor: "gray700",
          borderRadius: "999px",
          flex: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "2px 14px",
          height: pxToRem(48),
          gap: "0.5em"
        }}
        onSubmit={handleSubmit}
      >
        <Input
          sx={{
            fontSize: pxToRem(20),
            border: "none !important", // TODO: Better solution for inputs containing buttons
            "&::placeholder": {
              color: "gray4"
            },
            "&:focus": {
              outline: "none",
              border: "none"
            },
            textOverflow: "ellipsis",
            background: "none",
            padding: "0px !important",
            width: "100%",
            height: "auto"
          }}
          type="text"
          ref={inputRef}
          placeholder={placeholder}
          value={query || ""}
          onBlur={handleBlur}
          onFocus={onInputFocus}
          onChange={handleChange}
          spellCheck={spellCheck}
          {...getInputProps()}
        />
        <IconButton
          sx={{
            border: 0,
            padding: 0,
            fontSize: pxToRem(24),
            color: "black",
            background: "none",
            // @ts-ignore TS(1117): An object literal cannot have multiple properties ... Remove this comment to see the full error message
            padding: "0px !important",
            height: "auto",
            width: "auto"
          }}
          type="submit"
          icon={faSearch}
          screenReaderLabel={t("Submit search")}
        />
      </Form>
    );
  }
  if (!!useTutoringSearchUi) {
    return (
      <Form
        sx={{
          backgroundColor: theme.palette.common.white,
          border: "1px solid",
          borderColor: theme.palette.grey[600],
          borderRadius: "10rem",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem"
        }}
        onSubmit={handleSubmit}
      >
        <Icon
          icon={faSearch}
          sx={{
            fontSize: "2rem",
            color: "grey.600",
            marginLeft: "2rem"
          }}
        />
        <Input
          sx={{
            fontSize: pxToRem(20),
            border: "none !important", // TODO: Better solution for inputs containing buttons
            "&::placeholder": {
              color: theme.palette.grey[600]
            },
            "&:focus": {
              outline: "none",
              border: "none"
            },
            textOverflow: "ellipsis",
            background: "none",
            padding: "0px !important",
            width: "100%",
            height: "auto"
          }}
          type="text"
          ref={inputRef}
          placeholder={placeholder}
          value={query || ""}
          onBlur={handleBlur}
          onFocus={onInputFocus}
          onChange={handleChange}
          spellCheck={spellCheck}
          {...getInputProps()}
        />
        <Button
          color="primary"
          size="small"
          variant="contained"
          type="submit"
          sx={{ margin: "0.2rem" }}
        >
          <Typography>{t("Find tutoring")}</Typography>
        </Button>
      </Form>
    );
  }

  return (
    <>
      <Form
        sx={{
          backgroundColor: "white",
          border: "2px solid",
          borderColor: "black",
          borderRadius: "999px 0px 0px 999px",
          borderRight: "none",
          flex: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          height: "56px"
        }}
        onSubmit={handleSubmit}
      >
        <IconButton
          size="large"
          sx={{
            border: 0,
            height: "45px",
            paddingRight: 0,
            fontSize: "2.5rem",
            color: "black",
            background: "transparent",
            paddingLeft: "10px"
          }}
          type="submit"
          icon={faSearch}
          screenReaderLabel={t("Submit search")}
        />
        <Input
          sx={{
            width: "100%",
            flexGrow: 1,
            fontWeight: "500 !important",
            border: "none !important",
            "&::placeholder": {
              color: "gray4"
            },
            "&:focus": {
              outline: "none",
              border: "none"
            },
            textOverflow: "ellipsis"
          }}
          type="text"
          ref={inputRef}
          placeholder={placeholder}
          value={query || ""}
          onBlur={handleBlur}
          onFocus={onInputFocus}
          onChange={handleChange}
          spellCheck={spellCheck}
          {...getInputProps()}
        />
        {query && (
          <IconButton
            size="large"
            variant="link"
            sx={{
              fontSize: "2.5rem",
              color: "grey",
              background: "transparent",
              paddingX: "10px"
            }}
            onClick={handleClear}
            icon={faTimes}
            screenReaderLabel={t("Clear")}
          />
        )}
      </Form>
      <Button
        variant="contained"
        onClick={() => onSubmit && onSubmit(query)}
        sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
      >{t`Search`}</Button>
    </>
  );
}
